import React, { useMemo, useState } from 'react';
import { Modal, Tooltip } from 'antd';
import moment from 'moment';
import {
  Eye,
  FileText,
  Image as ImageIcon,
  MousePointerClick,
  Activity,
  Clock,
  Files,
  Smartphone,
  Tablet,
  Monitor,
  ArrowRight,
} from 'lucide-react';

import Spinner from '../../../../../../shared/designSystem/components/Spinner';

// =============================================================================
// SessionJourneyExpand (V2 Sessions) — facelifted to mirror V1's unified
// merged chronological timeline. V2's data shape differs from V1:
//   • No app-store / sitename macros → no "Served on - foo.com" /
//     "Served via - foo.com" prefix, no Application/Website/Ad-Server
//     indicator, no "Published by" attribution.
//   • No raw user-agent string → no UA tooltip on the platform pill.
//   • DOES carry `impressionPublisherMedia` (real publisher logo URL),
//     unlike V1's S3-report payload which never has it.
//   • Journey events use lowercase fields (`event.event` / `event.url` /
//     `event.dateTime`) instead of V1's uppercase legacy macros.
//
// Layout matches V1 as closely as the data allows: SummaryStrip on top,
// merged events list below (impressions + journey sorted chronologically).
// Loading overlay (V2 lazy-loads detail on expand) wraps the timeline so
// the SummaryStrip stays visible from session metadata available on the
// row record.
// =============================================================================

const secondsToTimeText = (seconds) => {
  if (seconds < 0 || !Number.isFinite(seconds)) return '—';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  const parts = [];
  if (hrs > 0) parts.push(`${hrs}h`);
  if (mins > 0) parts.push(`${mins}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
  return parts.join(' ');
};

const normalizeImpression = (impression, i) => {
  const {
    impressionTime,
    impressionPublisher,
    impressionPublisherUrl,
    impressionPublisherPlatform,
    impressionPubliserViewed,
    impressionPublisherMedia,
    media,
  } = impression;

  // V2 doesn't carry the macro chain V1 builds. Best title is the
  // publisher name; fall back to the Publisher URL when name is missing.
  const title = impressionPublisher || impressionPublisherUrl || 'Impression';

  // Two distinct fields on V2:
  //   • device = `impressionPubliserViewed` — OS classification (iPhone,
  //     Android, Desktop, etc.). Drives the device icon.
  //   • publisherPlatform = `impressionPublisherPlatform` — publisher-side
  //     platform classification (e.g. Mobile / Web / Application).
  //     Surfaced as a separate pill on the row.
  const device = impressionPubliserViewed || null;
  const publisherPlatform = impressionPublisherPlatform || null;

  return {
    type: 'Impression',
    timestamp: impressionTime,
    title,
    subtitle: null,
    link: impressionPublisherUrl || null,
    platform: device,
    publisherPlatform,
    creativeUrl: media || null,
    publisherMedia: impressionPublisherMedia || null,
    eventId: `imp-${i}`,
  };
};

const eventTypeLabel = (eventName) => {
  if (!eventName) return 'Event';
  const lower = String(eventName).toLowerCase();
  if (lower === 'page_view') return 'Page View';
  if (lower === 'click') return 'Click';
  if (lower === 'scroll') return 'Scroll';
  if (lower === 'session_start') return 'Session Start';
  if (lower === 'session_end') return 'Session End';
  if (lower === 'submit') return 'Submit';
  return eventName
    .toString()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const normalizeJourneyEvent = (event, i) => {
  const rawType = event.event || event.EVENT;
  return {
    type: eventTypeLabel(rawType),
    rawType: String(rawType || '').toLowerCase(),
    timestamp: event.dateTime || event.DATE,
    title: event.url || event.PAGE_URL || '—',
    subtitle: null,
    link: null,
    platform: null,
    creativeUrl: null,
    eventId: `evt-${i}`,
  };
};

const TYPE_META = {
  Impression: { Icon: Eye, tone: 'impression' },
  'Page View': { Icon: FileText, tone: 'page-view' },
  Click: { Icon: MousePointerClick, tone: 'click' },
};
const FALLBACK_META = { Icon: Activity, tone: 'event' };

const TypePill = ({ type }) => {
  const meta = TYPE_META[type] || FALLBACK_META;
  const { Icon, tone } = meta;
  return (
    <span
      className={`campaign-sessions-v1__journey-type campaign-sessions-v1__journey-type--${tone}`}
    >
      <Icon size={11} strokeWidth={2} />
      <span>{type}</span>
    </span>
  );
};

const DeviceIcon = ({ device }) => {
  if (device === 'Mobile') return <Smartphone size={13} strokeWidth={1.75} />;
  if (device === 'Tablet') return <Tablet size={13} strokeWidth={1.75} />;
  return <Monitor size={13} strokeWidth={1.75} />;
};

const SummaryStrip = ({
  pages,
  durationInSeconds,
  device,
  landingPage,
  exitPage,
  sessionStart,
}) => (
  <div className="campaign-sessions-v1__journey-summary">
    {sessionStart && (
      <span className="campaign-sessions-v1__journey-summary-item">
        <Clock size={13} strokeWidth={1.75} />
        <span>{moment(sessionStart).format('MMM DD, YYYY · HH:mm:ss')}</span>
      </span>
    )}
    <span className="campaign-sessions-v1__journey-summary-item">
      <Files size={13} strokeWidth={1.75} />
      <span>
        {pages} {pages === 1 ? 'page' : 'pages'}
      </span>
    </span>
    {Number.isFinite(durationInSeconds) && (
      <span className="campaign-sessions-v1__journey-summary-item">
        <Activity size={13} strokeWidth={1.75} />
        <span>{secondsToTimeText(durationInSeconds)}</span>
      </span>
    )}
    {device && (
      <span className="campaign-sessions-v1__journey-summary-item">
        <DeviceIcon device={device} />
        <span>{device}</span>
      </span>
    )}
    {(landingPage || exitPage) && (
      <span className="campaign-sessions-v1__journey-summary-item campaign-sessions-v1__journey-summary-flow">
        <Tooltip overlayClassName="mjl-ds" title={landingPage || '—'}>
          <span className="campaign-sessions-v1__journey-summary-page">
            {landingPage || '—'}
          </span>
        </Tooltip>
        <ArrowRight size={11} strokeWidth={2} />
        <Tooltip overlayClassName="mjl-ds" title={exitPage || '—'}>
          <span className="campaign-sessions-v1__journey-summary-page">
            {exitPage || '—'}
          </span>
        </Tooltip>
      </span>
    )}
  </div>
);

const SessionJourneyExpand = ({
  impressions = [],
  journey = [],
  loading = false,
  sessionStart,
  durationInSeconds,
  landingPage,
  exitPage,
  device,
  pageViewsHint,
}) => {
  const events = useMemo(() => {
    const fromImpressions = (impressions || []).map(normalizeImpression);
    const fromJourney = (journey || [])
      .filter((event) => {
        const t = String(event.event || event.EVENT || '').toLowerCase();
        return t !== 'record';
      })
      .map(normalizeJourneyEvent);
    const merged = [...fromImpressions, ...fromJourney];
    return merged.sort(
      (a, b) => new Date(a.timestamp).valueOf() - new Date(b.timestamp).valueOf()
    );
  }, [impressions, journey]);

  // Page count comes from the journey when loaded; while waiting, fall
  // back to the row-record `pageViews` already known from the table.
  const pageCount = useMemo(() => {
    if (journey && journey.length) {
      return journey.reduce((acc, curr) => {
        const t = String(curr.event || curr.EVENT || '').toLowerCase();
        return t === 'page_view' ? acc + 1 : acc;
      }, 0);
    }
    return Number.isFinite(pageViewsHint) ? pageViewsHint : 0;
  }, [journey, pageViewsHint]);

  const [previewCreative, setPreviewCreative] = useState(null);

  return (
    <div className="campaign-sessions-v1__journey">
      <Modal
        rootClassName="mjl-ds"
        title="Impression"
        visible={!!previewCreative}
        footer={null}
        onCancel={() => setPreviewCreative(null)}
        width={800}
        centered
      >
        {previewCreative?.creativeUrl && (
          <img
            src={previewCreative.creativeUrl}
            alt={previewCreative.eventId || 'Impression'}
            style={{ width: '100%', display: 'block', borderRadius: 8 }}
          />
        )}
        {previewCreative?.link && (
          <div
            style={{
              marginTop: 16,
              padding: 12,
              borderRadius: 8,
              background: 'rgba(15, 23, 42, 0.04)',
            }}
          >
            <div
              style={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: '#94a3b8',
                fontWeight: 600,
                marginBottom: 4,
              }}
            >
              Click Through URL
            </div>
            <a
              href={previewCreative.link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ wordBreak: 'break-all', fontSize: 13 }}
            >
              {previewCreative.link}
            </a>
          </div>
        )}
      </Modal>

      <SummaryStrip
        pages={pageCount}
        durationInSeconds={durationInSeconds}
        device={device}
        landingPage={landingPage}
        exitPage={exitPage}
        sessionStart={sessionStart}
      />

      <Spinner spinning={loading}>
        {events.length === 0 ? (
          <div className="campaign-sessions-v1__journey-empty">
            {loading ? 'Loading session journey…' : 'No events recorded for this session.'}
          </div>
        ) : (
          <ol className="campaign-sessions-v1__journey-list">
            {events.map((event, i) => (
              <li key={event.eventId || i} className="campaign-sessions-v1__journey-row">
                <span className="campaign-sessions-v1__journey-time">
                  {event.timestamp
                    ? moment(event.timestamp).format('HH:mm:ss')
                    : '—'}
                </span>

                <span className="campaign-sessions-v1__journey-rail" aria-hidden="true">
                  <span className="campaign-sessions-v1__journey-dot" />
                </span>

                {event.type === 'Impression' && (
                  <div
                    className={
                      'campaign-sessions-v1__journey-creative' +
                      (event.creativeUrl ? '' : ' campaign-sessions-v1__journey-creative--static')
                    }
                    onClick={() => event.creativeUrl && setPreviewCreative(event)}
                    role={event.creativeUrl ? 'button' : undefined}
                    tabIndex={event.creativeUrl ? 0 : undefined}
                    aria-label={
                      event.creativeUrl ? `Preview impression ${event.eventId || ''}` : undefined
                    }
                    data-no-expand
                  >
                    <div
                      className="campaign-overview__creatives-thumb"
                      style={
                        event.creativeUrl
                          ? { backgroundImage: `url("${event.creativeUrl}")` }
                          : undefined
                      }
                    >
                      {!event.creativeUrl && (
                        <ImageIcon size={18} color="#94a3b8" strokeWidth={1.75} />
                      )}
                    </div>
                  </div>
                )}

                <div className="campaign-sessions-v1__journey-body">
                  {event.type !== 'Impression' && (
                    <div className="campaign-sessions-v1__journey-head">
                      <TypePill type={event.type} />
                    </div>
                  )}

                  {event.type === 'Impression' ? (
                    <div className="campaign-sessions-v1__journey-impression-row">
                      <TypePill type={event.type} />
                      {event.platform && (
                        <div className="campaign-sessions-v1__journey-col">
                          <span className="campaign-sessions-v1__journey-col-eyebrow">
                            Device
                          </span>
                          <Tooltip overlayClassName="mjl-ds" title={event.platform}>
                            <span className="campaign-sessions-v1__journey-col-value">
                              <DeviceIcon device={event.platform} />
                              <span style={{ marginLeft: 6 }}>{event.platform}</span>
                            </span>
                          </Tooltip>
                        </div>
                      )}

                      <div className="campaign-sessions-v1__journey-col">
                        <span className="campaign-sessions-v1__journey-col-eyebrow">
                          Served on
                        </span>
                        <span className="campaign-sessions-v1__journey-col-value campaign-sessions-v1__journey-publisher">
                          <div
                            className="campaign-overview__creatives-thumb"
                            style={
                              event.publisherMedia
                                ? { backgroundImage: `url("${event.publisherMedia}")` }
                                : undefined
                            }
                          >
                            {!event.publisherMedia && (
                              <ImageIcon size={18} color="#94a3b8" strokeWidth={1.75} />
                            )}
                          </div>
                          <Tooltip overlayClassName="mjl-ds" title={event.title}>
                            <span className="campaign-sessions-v1__journey-publisher-title">
                              {event.title}
                            </span>
                          </Tooltip>
                        </span>
                      </div>

                      {event.publisherPlatform && (
                        <div className="campaign-sessions-v1__journey-col">
                          <span className="campaign-sessions-v1__journey-col-eyebrow">
                            Platform
                          </span>
                          <Tooltip overlayClassName="mjl-ds" title={event.publisherPlatform}>
                            <span className="campaign-sessions-v1__journey-col-value">
                              {event.publisherPlatform}
                            </span>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Tooltip overlayClassName="mjl-ds" title={event.title}>
                      <div className="campaign-sessions-v1__journey-title">
                        {event.title}
                      </div>
                    </Tooltip>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </Spinner>
    </div>
  );
};

export default SessionJourneyExpand;
